import * as yup from 'yup'

export const owdUpdateAsnItemsXmlSchema = yup.object({
  ASNITEM_SKU: yup.string().required(),
  ASNITEM_EXPECTED: yup.number().required(),
  ASNITEM_DESCRIPTION: yup.string().nullable(),
})

export type OwdAsnItemXml = yup.InferType<typeof owdUpdateAsnItemsXmlSchema>

export const owdUpdateAsnXmlInputSchema = yup.object({
  ID: yup.number().required(),
  STATUS: yup.string().nullable(),
  REFERENCE: yup.string().nullable(),
  PO_REFERENCE: yup.string().nullable(),
  SHIPPER: yup.string().nullable(),
  EXPECTED_DATE: yup.string().nullable(),
  CARRIER: yup.string().nullable(),
  AUTORELEASE: yup.boolean().nullable(),
  CARTONS: yup.number().nullable(),
  PALLETS: yup.number().nullable(),
  NOTES: yup.string().nullable(),
  ASNITEMS: yup.array().of(owdUpdateAsnItemsXmlSchema).notRequired(),
})

export type OwdUpdateAsnXml = yup.InferType<typeof owdUpdateAsnXmlInputSchema>
export const owdAsnItemInputXmlSchema = yup.object({
  ASNITEM_SKU: yup.string().required(),
  ASNITEM_EXPECTED: yup.number().required(),
  ASNITEM_DESCRIPTION: yup.string().required(),
})

export type OwdAsnItemInputXml = yup.InferType<typeof owdAsnItemInputXmlSchema>

export const owdCreateAsnInputXmlSchema = yup.object({
  REFERENCE: yup.string().nullable(),
  PO_REFERENCE: yup.string().nullable(),
  SHIPPER: yup.string().nullable(),
  EXPECTED_DATE: yup.string().required(),
  CARRIER: yup.string().nullable(),
  AUTORELEASE: yup.boolean().required(),
  CARTONS: yup.number().nullable(),
  PALLETS: yup.number().nullable(),
  NOTES: yup.string().nullable(),
  ASNITEMS: yup.array().of(owdAsnItemInputXmlSchema).required(),
})

export type OwdCreateAsnInputXml = yup.InferType<typeof owdCreateAsnInputXmlSchema>

export const owdCancelOrderXmlInputSchema = yup.object({
  clientOrderId: yup.string().required(),
})

export type OwdCancelOrderXml = yup.InferType<typeof owdCancelOrderXmlInputSchema>

export const booleanDefaultFalse = yup
  .boolean()
  .transform((value, originalValue) => {
    if (originalValue == null || originalValue === '') {
      return false
    }
    return value
  })
  .default(false)

export const owdHoldOrderXmlInputSchema = yup.object({
  clientOrderId: yup.string().required(),
})

export type OwdHoldOrderXml = yup.InferType<typeof owdHoldOrderXmlInputSchema>

export const owdGetAsnStatusXmlInputSchema = yup.object({
  reference: yup.string().nullable().optional(),
  containsSku: yup.string().nullable().optional(),
  status: yup
    .string()
    .oneOf(['UNRECEIVED', 'PARTIALRECEIPT', 'CANCELLED', 'COMPLETE'])
    .nullable()
    .optional(),
  receivedOn: yup.string().nullable().optional(),
  receivedSince: yup.string().nullable().optional(),
  hasUnreleasedReceives: yup.boolean().nullable().optional(),
  owdReference: yup.number().nullable().optional(),
  poReference: yup.string().nullable().optional(),
  shipper: yup.string().nullable().optional(),
  groupName: yup.string().nullable().optional(),
})

export type OwdGetAsnStatusXml = yup.InferType<typeof owdGetAsnStatusXmlInputSchema>

export const owdDeleteAsnXmlInputSchema = yup.object({
  id: yup.string().required('ASN ID is required'),
})

export type OwdDeleteAsnXml = yup.InferType<typeof owdDeleteAsnXmlInputSchema>

export const owdInventoryComponentXmlSchema = yup.object({
  COMPONENT_SKU: yup.string().nullable(),
  COMPONENT_QTY: yup.number().nullable(),
})

export type OwdInventoryComponentXml = yup.InferType<typeof owdInventoryComponentXmlSchema>

export const owdCreateInventoryXmlInputSchema = yup.object({
  SKU: yup.string().required(),
  TYPE: yup.string().required(),
  SUPPLIER: yup.string().nullable(),
  SUPPLIER_SKU: yup.string().nullable(),
  KEYWORD: yup.string().nullable(),
  UNIT_PRICE: yup.number().nullable(),
  UNIT_COST: yup.number().nullable(),
  TITLE: yup.string().nullable(),
  DESCRIPTION: yup.string().nullable(),
  COLOR: yup.string().nullable(),
  SIZE: yup.string().nullable(),
  REORDER_ALERT_QTY: yup.number().nullable(),
  WEIGHT: yup.number().nullable(),
  CUSTOMS_DESC: yup.string().nullable(),
  CUSTOMS_VALUE: yup.number().nullable(),
  PACKING_INSTRUCTIONS: yup.string().nullable(),
  NOTES: yup.string().nullable(),
  IMAGE_URL: yup.string().nullable(),
  THUMB_URL: yup.string().nullable(),
  WEB_URL: yup.string().nullable(),
  GROUPNAME: yup.string().nullable(),
  CASE_QTY: yup.number().nullable(),
  MASTER_CASE_QTY: yup.number().nullable(),
  DEFAULT_FACILITY_CODE: yup.string().nullable(),
  COMPONENTS: yup
    .array()
    .of(owdInventoryComponentXmlSchema)
    .transform((value): unknown =>
      Array.isArray(value)
        ? value.filter(
            (item): item is { COMPONENT_SKU?: string | null; COMPONENT_QTY?: number | null } =>
              typeof item === 'object' &&
              item !== null &&
              ('COMPONENT_SKU' in item || 'COMPONENT_QTY' in item),
          )
        : value,
    )
    .notRequired(),
})

export type OwdCreateInventoryItemXml = yup.InferType<typeof owdCreateInventoryXmlInputSchema>

export const owdGetInventoryCountXmlInputSchema = yup.object({
  part_reference: yup.string().nullable().optional(),
  show_active: booleanDefaultFalse,
  show_backorders: booleanDefaultFalse,
  show_description: booleanDefaultFalse,
})

export type OwdGetInventoryCountXml = yup.InferType<typeof owdGetInventoryCountXmlInputSchema>

export const owdGetShippingRatesXmlInputSchema = yup.object({
  shipping_address: yup.object({
    address1: yup.string().nullable().optional(),
    address2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    region: yup.string().nullable().optional(),
    postCode: yup.string().nullable().optional(),
    countryCode: yup.string().nullable().optional(),
  }),
  facilityCode: yup.string().required(),
  items: yup
    .array()
    .of(
      yup.object({
        sku: yup.string().required('SKU is required'),
        qty: yup
          .number()
          .typeError('Quantity must be a number')
          .required('Quantity is required')
          .min(1, 'Quantity must be at least 1'),
      }),
    )
    .nullable()
    .optional(),
  shipMethods: yup
    .array()
    .of(yup.string().required('Method Code is required'))
    .min(1, 'At least one Shipping Method is required')
    .required('Shipping Methods is required'),
})

export type OwdGetShippingRatesXml = yup.InferType<typeof owdGetShippingRatesXmlInputSchema>

export const owdGetInventoryItemStatusXmlInputSchema = yup.object({
  sku: yup.string().nullable().optional(),
  skuContains: yup.string().nullable().optional(),
  type: yup.string().oneOf(['KIT', 'VIRTUAL', 'SERIALIZED', 'PHYSICAL']).nullable().optional(),
  containsComponent: yup.string().nullable().optional(),
  isActive: yup.boolean().nullable().optional(),
  stockLevelBelow: yup.number().nullable().optional(),
  groupName: yup.string().nullable().optional(),
})

export type OwdGetInventoryItemStatusXml = yup.InferType<
  typeof owdGetInventoryItemStatusXmlInputSchema
>

export const owdGetInventoryReportXmlInputSchema = yup.object({
  type: yup
    .string()
    .oneOf(['RETURN', 'RECEIVE', 'ADJUSTMENT', 'ALL'])
    .default('ALL')
    .nullable()
    .optional(),
  containsSku: yup.string().nullable().optional(),
  facilityCode: yup.string().nullable().optional(),
  startDate: yup.string().nullable().optional(),
  endDate: yup.string().nullable().optional(),
})

export type OwdGetInventoryReportXml = yup.InferType<typeof owdGetInventoryReportXmlInputSchema>

export const owdReleaseAsnXmlInputSchema = yup.object({
  id: yup.number().required('ASN ID is required'),
})

export type OwdReleaseAsnXml = yup.InferType<typeof owdReleaseAsnXmlInputSchema>

export const owdReleaseOrderXmlInputSchema = yup.object({
  clientOrderId: yup.string().required(),
})

export type OwdReleaseOrderXml = yup.InferType<typeof owdReleaseOrderXmlInputSchema>

export const createOrderLineItemXmlSchema = yup.object({
  part_reference: yup.string().required(),
  description: yup.string().required(),
  requested: yup.number().required(),
  cost: yup.number().required(),
  declared_value: yup.number().nullable(),
  customs_desc: yup.string().nullable(),
  backordered: yup.number().nullable(),
  external_line_id: yup.string().nullable(),
  is_insert_item: yup.string().nullable(),
})

export type CreateOrderLineItemXml = yup.InferType<typeof createOrderLineItemXmlSchema>

export const orderCustomValueXmlSchema = yup.object({
  name: yup.string().nullable(),
  value: yup.string().nullable(),
})

export type OrderCustomValueXml = yup.InferType<typeof orderCustomValueXmlSchema>

export const shippingInfoXmlSchema = yup.object({
  last_name: yup.string().nullable(),
  first_name: yup.string().nullable(),
  company_name: yup.string().nullable(),
  address_one: yup.string().required(),
  address_two: yup.string().nullable(),
  city: yup.string().required(),
  state: yup.string().required(),
  zip: yup.string().required(),
  country: yup.string().required(),
  phone: yup.string().nullable(),
  email: yup.string().nullable(),
  ship_type: yup.string().required(),
  ship_cost: yup.number().required(),
  insure_amount: yup.number().nullable(),
  declared_value: yup.number().nullable(),
  customs_desc: yup.string().nullable(),
  terms: yup
    .mixed<'SHIPPER' | 'CONSIGNEE'>()
    .oneOf(['SHIPPER', 'CONSIGNEE'])
    .default('SHIPPER')
    .nullable(),
  acct: yup.string().nullable(),
  billing_ref: yup.string().nullable(),
  call_tag_desc: yup.string().nullable(),
  cod_amount: yup.number().nullable(),
  cod_payment_type: yup.string().nullable(),
  cod_add_cost: yup.number().nullable(),
  cod_funds_contact: yup.string().nullable(),
  cod_funds_company: yup.string().nullable(),
  cod_funds_address_1: yup.string().nullable(),
  cod_funds_address_2: yup.string().nullable(),
  cod_funds_city: yup.string().nullable(),
  cod_funds_state: yup.string().nullable(),
  cod_funds_zip: yup.string().nullable(),
  proof_of_delivery: yup.string().nullable(),
  saturday_delivery: yup.string().nullable(),
})

export type ShippingInfoXml = yup.InferType<typeof shippingInfoXmlSchema>

export const billingInfoXmlSchema = yup.object({
  last_name: yup.string().nullable(),
  first_name: yup.string().nullable(),
  company_name: yup.string().nullable(),
  address_one: yup.string().required(),
  address_two: yup.string().nullable(),
  city: yup.string().required(),
  state: yup.string().required(),
  zip: yup.string().required(),
  country: yup.string().required(),
  phone: yup.string().nullable(),
  email: yup.string().nullable(),
  tax_pct: yup.number().nullable(),
  tax_amount: yup.number().nullable(),
  po_number: yup.string().nullable(),
  paid: yup.mixed<'YES' | 'NO'>().oneOf(['YES', 'NO']).default('YES').nullable(),
  paid_date: yup.string().nullable(),
  payment_type: yup
    .mixed<'CC' | 'CK' | 'PO' | 'CLIENT'>()
    .oneOf(['CC', 'CK', 'PO', 'CLIENT'])
    .default('CLIENT'),
  acct_no: yup.string().nullable(),
  exp_year: yup.string().nullable(),
  exp_month: yup.string().nullable(),
  order_discount: yup.number().nullable(),
  auth_amount: yup.number().nullable(),
  auth_code: yup.string().nullable(),
  auth_trans: yup.string().nullable(),
})

export type BillingInfoXml = yup.InferType<typeof billingInfoXmlSchema>

export const owdCreateOrderXmlInputSchema = yup.object({
  order_reference: yup.string().required(),
  order_source: yup.string().nullable(),
  is_gift: yup.string().nullable(),
  backorder_rule: yup
    .mixed<'BACKORDER' | 'PARTIALSHIP' | 'IGNOREBACKORDER' | 'HOLDORDER' | 'NOBACKORDER'>()
    .oneOf(['BACKORDER', 'PARTIALSHIP', 'IGNOREBACKORDER', 'HOLDORDER', 'NOBACKORDER'])
    .default('NOBACKORDER'),
  facility_rule: yup.string().nullable(),
  hold_for_release: yup.string().nullable(),
  group_name: yup.string().nullable(),
  SHIPPING_INFO: shippingInfoXmlSchema.required(),
  BILLING_INFO: billingInfoXmlSchema.required(),
  LINE_ITEM: yup.array().of(createOrderLineItemXmlSchema).min(1).required(),
  MESSAGE: yup.string().nullable(),
  COMMENTS: yup.string().nullable(),
  WAREHOUSENOTES: yup.string().nullable(),
  CUSTOM_VALUE: yup.array().of(orderCustomValueXmlSchema).nullable(),
})

export type OwdCreateOrderXml = yup.InferType<typeof owdCreateOrderXmlInputSchema>

export const owdUpdateInventoryItemXmlInputSchema = yup.object({
  SKU: yup.string().required(),
  SUPPLIER: yup.string().nullable(),
  SUPPLIER_SKU: yup.string().nullable(),
  KEYWORD: yup.string().nullable(),
  UNIT_PRICE: yup.number().nullable(),
  UNIT_COST: yup.number().nullable(),
  TITLE: yup.string().nullable(),
  DESCRIPTION: yup.string().nullable(),
  COLOR: yup.string().nullable(),
  SIZE: yup.string().nullable(),
  REORDER_ALERT_QTY: yup.number().nullable(),
  WEIGHT: yup.number().nullable(),
  CUSTOMS_DESC: yup.string().nullable(),
  CUSTOMS_VALUE: yup.number().nullable(),
  PACKING_INSTRUCTIONS: yup.string().nullable(),
  NOTES: yup.string().nullable(),
  IMAGE_URL: yup.string().nullable(),
  THUMB_URL: yup.string().nullable(),
  WEB_URL: yup.string().nullable(),
  GROUPNAME: yup.string().nullable(),
  CASE_QTY: yup.number().nullable(),
  MASTER_CASE_QTY: yup.number().nullable(),
  DEFAULT_FACILITY_CODE: yup.string().nullable(),
  COMPONENTS: yup
    .array()
    .of(
      yup.object({
        COMPONENT_SKU: yup.string().nullable(),
        COMPONENT_QTY: yup.number().nullable(),
      }),
    )
    .notRequired(),
})

export type OwdUpdateInventoryItemXml = yup.InferType<typeof owdUpdateInventoryItemXmlInputSchema>

const updateOrderLineItemXmlSchema = yup.object({
  part_reference: yup.string().nullable(),
  description: yup.string().nullable(),
  requested: yup.number().nullable(),
  cost: yup.number().nullable(),
  line_number: yup.number().nullable(),
})

export type UpdateOrderLineItemXml = yup.InferType<typeof updateOrderLineItemXmlSchema>

export const owdUpdateOrderXmlInputSchema = yup.object({
  clientOrderId: yup.string().required(),
  ADD_NOTE: yup.string().nullable(),
  BILL_NAME: yup.string().nullable(),
  BILL_COMPANY: yup.string().nullable(),
  BILL_ADDRESS_ONE: yup.string().nullable(),
  BILL_ADDRESS_TWO: yup.string().nullable(),
  BILL_CITY: yup.string().nullable(),
  BILL_STATE: yup.string().nullable(),
  BILL_POSTCODE: yup.string().nullable(),
  BILL_COUNTRY: yup.string().nullable(),
  BILL_PHONE: yup.string().nullable(),
  BILL_EMAIL: yup.string().nullable(),
  BILL_PO: yup.string().nullable(),
  SHIP_NAME: yup.string().nullable(),
  SHIP_COMPANY: yup.string().nullable(),
  SHIP_ADDRESS_ONE: yup.string().nullable(),
  SHIP_ADDRESS_TWO: yup.string().nullable(),
  SHIP_CITY: yup.string().nullable(),
  SHIP_STATE: yup.string().nullable(),
  SHIP_POSTCODE: yup.string().nullable(),
  SHIP_COUNTRY: yup.string().nullable(),
  SHIP_PHONE: yup.string().nullable(),
  SHIP_EMAIL: yup.string().nullable(),
  TOTAL_SHIPHANDLING: yup.number().nullable(),
  TOTAL_TAX: yup.number().nullable(),
  TOTAL_DISCOUNT: yup.number().nullable(),
  ORDER_COMMENT: yup.string().nullable(),
  ORDER_WAREHOUSENOTES: yup.string().nullable(),
  FACILITY_RULE: yup.string().nullable(),
  SHIPPING_METHOD: yup.string().nullable(),
  GROUP_NAME: yup.string().nullable(),
  CUSTOM_VALUE: yup.array().of(orderCustomValueXmlSchema).nullable().optional(),
  LINE_ITEM: yup.array().of(updateOrderLineItemXmlSchema).notRequired(),
})

export type OwdUpdateOrderXml = yup.InferType<typeof owdUpdateOrderXmlInputSchema>

export const owdGetOrderSummaryXmlInputSchema = yup.object({
  clientOrderId: yup.string().required(),
})

export type OwdGetOrderSummaryXml = yup.InferType<typeof owdGetOrderSummaryXmlInputSchema>

export const owdGetOrderStatusXmlStepInputSchema = yup
  .object({
    clientOrderId: yup.string().nullable().optional(),
    createdDate: yup
      .string()
      .nullable()
      .optional()
      .when('clientOrderId', {
        is: (val: string | null | undefined) => !!val,
        then: (schema) => schema.transform(() => null),
      }),
    prefixSearch: yup.boolean().nullable().optional(),
  })
  .test(
    'clientOrderId-or-createdDate',
    'Either clientOrderId or createdDate must be provided.',
    (value) => {
      const { clientOrderId, createdDate } = value || {}
      return Boolean(clientOrderId || createdDate)
    },
  )

export type OwdGetOrderStatusXml = yup.InferType<typeof owdGetOrderStatusXmlStepInputSchema>
